/*
 * @Description: 告警规则数据 hook（G3 读取 / G4 保存）。
 *   弹窗打开（enabled）时拉 /config/alert-rules/get，save 按 ruleType 逐条
 *   /config/alert-rules/save（幂等 upsert）。mock 模式读默认值、保存直接成功。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import {
  fetchAlertRules,
  saveAlertRule
} from '../../../actions/locationCockpit'
import {
  buildAlertRuleSaves,
  DEFAULT_ALERT_RULES,
  isCockpitMockEnabled,
  parseAlertRules
} from '../constants'
import { CockpitEnvelope } from '../types/cockpit.types'
import { AlertRulesData, AlertRulesDraft } from '../types/settings.types'

export interface UseAlertRulesResult {
  rules: AlertRulesDraft | null
  loading: boolean
  saving: boolean
  /** 保存三条规则（continuous_distance / stay_timeout / offline），全部成功才算成功 */
  save: (next: AlertRulesDraft) => Promise<boolean>
}

/**
 * 告警规则读取 / 保存。
 *
 * @param groupId - 当前团队长 id
 * @param enabled - 是否启用（弹窗打开时才拉取）
 */
export function useAlertRules(
  groupId: string,
  enabled: boolean
): UseAlertRulesResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [rules, setRules] = useState<AlertRulesDraft | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    if (isMock) {
      setRules({ ...DEFAULT_ALERT_RULES })
      return
    }
    if (!groupId) return
    setLoading(true)
    dispatch(fetchAlertRules({ groupId }))
      .then((res: CockpitEnvelope<AlertRulesData>) => {
        if (!aliveRef.current) return
        setLoading(false)
        // 1002/1004 空态 data 为 null，parseAlertRules 用默认值兜底全四类
        setRules(
          parseAlertRules(
            res && res.code === 200 ? (res.data?.items ?? []) : []
          )
        )
      })
      .catch(() => {
        if (!aliveRef.current) return
        setLoading(false)
        setRules({ ...DEFAULT_ALERT_RULES })
      })
  }, [dispatch, groupId, enabled, isMock])

  const save = useCallback(
    (next: AlertRulesDraft): Promise<boolean> => {
      if (isMock) {
        setRules(next)
        return Promise.resolve(true)
      }
      if (!groupId) return Promise.resolve(false)
      setSaving(true)
      const saves = buildAlertRuleSaves(next).map((payload) =>
        dispatch(saveAlertRule({ ...payload, groupId })).then(
          (res: CockpitEnvelope<unknown>) => !!res && res.code === 200
        )
      )
      return Promise.all(saves)
        .then((results) => {
          if (aliveRef.current) {
            setSaving(false)
            setRules(next)
          }
          return results.every(Boolean)
        })
        .catch(() => {
          if (aliveRef.current) setSaving(false)
          return false
        })
    },
    [dispatch, groupId, isMock]
  )

  return { rules, loading, saving, save }
}
